import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    customer_type: '',
    vip_tier: '',
    customer_segment: '',
    is_active: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchAnalytics();
  }, [filters]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      // Build query params from filters
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.customer_type) params.append('customer_type', filters.customer_type);
      if (filters.vip_tier) params.append('vip_tier', filters.vip_tier);
      if (filters.customer_segment) params.append('customer_segment', filters.customer_segment);
      if (filters.is_active) params.append('is_active', filters.is_active);
      
      const response = await api.get(`/customers?${params.toString()}`);
      setCustomers(response.data.customers || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/customers/analytics');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const handleCreate = () => {
    navigate('/customers/new');
  };

  const handleEdit = (customerId) => {
    navigate(`/customers/${customerId}/edit`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await api.delete(`/customers/${id}`);
      setSuccess('Customer deleted');
      fetchCustomers();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      customer_type: '',
      vip_tier: '',
      customer_segment: '',
      is_active: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Customers</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              👥 Customers
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage customer information and interactions
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className={`btn ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-outline'}`}
            >
              🔍 {hasActiveFilters ? `Filters (${Object.values(filters).filter(v => v !== '').length})` : 'Filters'}
            </button>
            <button onClick={handleCreate} className="btn btn-primary">➕ Add Customer</button>
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
            {success}
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div style={{ 
            backgroundColor: 'var(--gray-50)', 
            border: '1px solid var(--gray-200)', 
            borderRadius: '8px', 
            padding: '1.5rem', 
            marginBottom: '1.5rem' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                🔍 Filter Customers
              </h3>
              {hasActiveFilters && (
                <button 
                  onClick={handleClearFilters} 
                  className="btn btn-sm btn-outline"
                  style={{ fontSize: '0.875rem' }}
                >
                  Clear All
                </button>
              )}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {/* Search */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Name, email, phone..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Customer Type */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Customer Type
                </label>
                <select
                  value={filters.customer_type}
                  onChange={(e) => handleFilterChange('customer_type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">All Types</option>
                  <option value="person">👤 Person</option>
                  <option value="company">🏢 Company</option>
                </select>
              </div>

              {/* VIP Tier */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  VIP Tier
                </label>
                <select
                  value={filters.vip_tier}
                  onChange={(e) => handleFilterChange('vip_tier', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">All Tiers</option>
                  <option value="BRONZE">Bronze</option>
                  <option value="SILVER">Silver</option>
                  <option value="GOLD">Gold</option>
                  <option value="PLATINUM">Platinum</option>
                </select>
              </div>

              {/* Customer Segment */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Segment
                </label>
                <select
                  value={filters.customer_segment}
                  onChange={(e) => handleFilterChange('customer_segment', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">All Segments</option>
                  <option value="NEW_PROSPECT">New Prospect</option>
                  <option value="FIRST_TIME_BUYER">First Time Buyer</option>
                  <option value="REPEAT_CUSTOMER">Repeat Customer</option>
                  <option value="LOYAL_CUSTOMER">Loyal Customer</option>
                  <option value="VIP">VIP</option>
                  <option value="AT_RISK">At Risk</option>
                  <option value="CHURNED">Churned</option>
                  <option value="WIN_BACK">Win Back</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Status
                </label>
                <select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="true">✅ Active</option>
                  <option value="false">❌ Inactive</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Cards */}
        {analytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Customers</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{analytics.total_customers}</div>
            </div>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Revenue</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>${analytics.total_revenue?.toLocaleString()}</div>
            </div>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Active Customers</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{analytics.active_customers}</div>
            </div>
          </div>
        )}

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Email</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Customer Type</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Location</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>VIP Tier</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Spent</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>No customers found</td></tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '0.75rem' }}>{customer.email}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {customer.customer_type === 'company' ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>🏢</span>
                        <span>Company</span>
                      </span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>👤</span>
                        <span>Person</span>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {customer.customer_type === 'company' 
                      ? (customer.company_name || '-')
                      : (`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '-')
                    }
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {[customer.location_city, customer.location_state, customer.location_country].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className={`badge badge-${customer.vip_tier === 'gold' ? 'warning' : customer.vip_tier === 'silver' ? 'secondary' : customer.vip_tier === 'bronze' ? 'info' : 'default'}`}>
                      {customer.vip_tier || 'none'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>${(customer.total_spent || 0).toLocaleString()}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button onClick={() => handleEdit(customer.id)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem' }}>Edit</button>
                    <button onClick={() => handleDelete(customer.id)} className="btn btn-sm btn-danger">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Customers;
